import {
  AccountTreeOutlined,
  AddOutlined,
  BusinessOutlined,
  ChevronLeftRounded,
  ChevronRightRounded,
  CloseOutlined,
  DeleteOutlineOutlined,
  DescriptionOutlined,
  EditOutlined,
  HouseOutlined,
  InfoOutlined,
  PeopleOutlined,
  PersonAddAltOutlined,
  PersonOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";

import EntityFilesTable from "./EntityFilesTable";
import Flx from "../../../../components/layout/Flx";
import SearchInput from "../../../../components/inputs/SearchInput";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import ConfirmationDialog from "../../../../components/ui/ConfirmationDialog";
import ResizeableSidebar from "../../../../components/sidebar/ResizeableSidebar";
import DocGroupCoverageCard from "../../shared/DocGroupCoverageCard";
import {
  CategoryDocTypeGrid,
  FixedUploadQueue,
} from "../LoanDrilldownDocumentsTab/LoanDrilldownDocumentsTab";
import {
  UploadQueueProvider,
  useUploadQueue,
} from "../LoanDrilldownDocumentsTab/UploadQueueContext";
import { SalesforceEntitySearchBase } from "../../../../components/salesforce/SalesforceEntitySearch";
// The entity + member panels are on the panel-form standard (§12b); the
// section model reads the descriptors below.
import {
  FormField,
  FormMethodCards,
  FormPanelFooter,
} from "../../../../components/forms/FormPrimitives";
import {
  PanelSections,
  SCOPE,
  changedOnly,
  countRequiredFilled,
  prune,
  requiredOf,
  stripKeys,
} from "../../../../components/forms/panelFormModel";
import { FORM, formSx } from "../../../../theme/formStyles";
import Htag from "../../../../components/typography/Htag";
import Txt from "../../../../components/typography/Txt";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import SectionEmptyState from "../../shared/tabLayout/SectionEmptyState";
import { TAB_TOKENS as T } from "../../shared/tabLayout/tabTokens";
import { selectOptionsEntityMemberTitle } from "../../../../constants/selectOptions/selectOptionsEntityMemberTitle";
import { selectOptionsEntityType } from "../../../../constants/selectOptions/selectOptionsEntityType";
import { selectOptionsUsaStates } from "../../../../constants/selectOptions/selectOptionsUsaStates";
import { useEntitiesHook } from "../../../../hooks/api/useEntitiesHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { useFileDocGroupAndTypesHook } from "../../../../hooks/useFileDocGroupAndTypesHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

/**
 * Loan Drilldown — Entity tab.
 *
 * Renders state.loanDrilldown.entities as a top-down ownership chart.
 * The backend stores entities as a flat array where index 0 is the
 * borrowing entity (bottom of the ownership chain) and each subsequent
 * index owns some of the one before it. The borrower is displayed at the
 * root of the chart with parent entities cascading as children; each
 * entity also shows its entityMembers (individuals).
 *
 * CRUD is wired through useEntitiesHook:
 *   - createLoanEntity      ({ borrowerEntity | nestedEntity })
 *   - updateEntityData      (entityId)
 *   - createLoanEntityMember(entityId)
 *   - updateLoanEntityMember(entityId, memberId)
 *   - deleteEntity          (entityId)
 *   - deleteEntityMember    (entityId, memberId)
 * All mutations dispatch loanDrilldownSet(response) on success.
 *
 * Deletes are leaf-up. The backend refuses (409) to delete an entity that
 * other entities still name as their parentEntityId, because that would
 * orphan the subtree, so the delete affordance is DISABLED on any entity
 * with children rather than letting the call fail. There is no undelete —
 * nothing is archived the way deletedBorrowers is — hence the confirm step.
 */
const LoanDrilldownEntityTab = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const entities = useSelector((state) => state.loanDrilldown?.entities);
  const borrowers = useSelector((state) => state.loanDrilldown?.borrowers);
  const legacyBorrowerEntity = useSelector(
    (state) => state.loanDrilldown?.borrowerEntity,
  );
  const loanId = useSelector((state) => state.loanDrilldown?._id);
  const { deleteEntity, deleteEntityMember } = useEntitiesHook();

  const root = useMemo(
    () => normalizeEntitiesArray(entities, borrowers),
    [entities, borrowers],
  );

  // Legacy shape: entities[] is empty but the deal still carries the
  // pre-migration borrowerEntity field. Offer the user a one-shot migration
  // to the flat model before creating a fresh entity.
  const needsMigration =
    (isNil(entities) || isEmpty(entities)) && !isNil(legacyBorrowerEntity);

  const [entityDialog, setEntityDialog] = useState(null);
  const [memberDialog, setMemberDialog] = useState(null);

  const openCreateBorrowerEntity = useCallback(
    () => setEntityDialog({ mode: "create-borrower" }),
    [],
  );
  const openCreateNestedEntity = useCallback(
    () => setEntityDialog({ mode: "create-nested" }),
    [],
  );
  const openCreateNestedOnParent = useCallback(
    (parentEntityId) =>
      setEntityDialog({ mode: "create-nested-on-parent", parentEntityId }),
    [],
  );
  const openEditEntity = useCallback(
    (entity) => setEntityDialog({ mode: "edit", entity }),
    [],
  );
  const openCreateMember = useCallback(
    (entityId) => setMemberDialog({ mode: "create", entityId }),
    [],
  );
  const openEditMember = useCallback(
    (entityId, member) => setMemberDialog({ mode: "edit", entityId, member }),
    [],
  );

  const closeEntityDialog = useCallback(() => setEntityDialog(null), []);
  const closeMemberDialog = useCallback(() => setMemberDialog(null), []);

  /* ── Delete ─────────────────────────────────────────────────────────
   * One confirm dialog serves both kinds; `deleteTarget` carries which.
   *   { kind: "entity",  entityId, name, isBorrowingEntity }
   *   { kind: "member",  entityId, memberId, name }
   */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openDeleteEntity = useCallback(
    (node) =>
      setDeleteTarget({
        kind: "entity",
        entityId: node?.entityId,
        name: node?.entityName || "this entity",
        isBorrowingEntity: Boolean(node?.isBorrowingEntity),
      }),
    [],
  );

  const openDeleteMember = useCallback(
    (entityId, node) =>
      setDeleteTarget({
        kind: "member",
        entityId,
        memberId: node?.memberId,
        name:
          node?.fullName ||
          [node?.firstName, node?.lastName].filter(Boolean).join(" ") ||
          "this individual",
      }),
    [],
  );

  const closeDeleteDialog = useCallback(() => setDeleteTarget(null), []);

  // Delete straight from an edit form's header. The form closes first — the
  // confirm dialog would otherwise sit underneath the sidebar's stacking layer.
  const handleDeleteFromEntityForm = useCallback(() => {
    const entity = entityDialog?.entity;
    if (!entity) return;
    closeEntityDialog();
    openDeleteEntity({
      entityId: entity._id,
      entityName: entity.entityName,
      isBorrowingEntity: entity.borrowerEntity,
    });
  }, [entityDialog, closeEntityDialog, openDeleteEntity]);

  const handleDeleteFromMemberForm = useCallback(() => {
    const member = memberDialog?.member;
    if (!member) return;
    closeMemberDialog();
    openDeleteMember(memberDialog?.entityId, {
      memberId: member._id,
      fullName: member.fullName,
      firstName: member.firstName,
      lastName: member.lastName,
    });
  }, [memberDialog, closeMemberDialog, openDeleteMember]);

  const handleConfirmDelete = useCallback(() => {
    if (!loanId || !deleteTarget) return;
    setDeleting(true);

    const onSuccessFn = (response) => {
      // The routes return the full refreshed deal — replace, don't merge.
      // The chart's selection resolves against the new tree, so the detail
      // panel for a deleted node closes on its own.
      if (response) dispatch(loanDrilldownSet(response));
      setDeleting(false);
      setDeleteTarget(null);
    };
    const onFailFn = (err) => {
      setDeleting(false);
      // 409 = the child guard. Expected client state, not a system error:
      // show the backend's own message and leave the dialog open so the
      // user can cancel out to clear the children first.
      const data = err?.response?.data;
      if (err?.response?.status === 409) {
        enqueueSnackbar(
          data?.message ||
            "Delete the nested entities this entity owns before deleting it.",
          { variant: "warning" },
        );
        setDeleteTarget(null);
      }
    };

    if (deleteTarget.kind === "entity") {
      deleteEntity({
        loanId,
        entityId: deleteTarget.entityId,
        onSuccessFn,
        onFailFn,
      });
      return;
    }
    deleteEntityMember({
      loanId,
      entityId: deleteTarget.entityId,
      memberId: deleteTarget.memberId,
      onSuccessFn,
      onFailFn,
    });
  }, [
    loanId,
    deleteTarget,
    deleteEntity,
    deleteEntityMember,
    dispatch,
    enqueueSnackbar,
  ]);

  if (!root) {
    return (
      <>
        <EmptyState
          onCreate={openCreateBorrowerEntity}
          needsMigration={needsMigration}
          loanId={loanId}
        />
        <EntityFormDialog
          dialog={entityDialog}
          loanId={loanId}
          onClose={closeEntityDialog}
        />
      </>
    );
  }

  return (
    <>
      <EntityOrgChart
        root={root}
        onAddNestedEntity={openCreateNestedEntity}
        onAddNestedOnParent={openCreateNestedOnParent}
        onEditEntity={openEditEntity}
        onAddMember={openCreateMember}
        onEditMember={openEditMember}
        onDeleteEntity={openDeleteEntity}
        onDeleteMember={openDeleteMember}
      />
      <EntityFormDialog
        dialog={entityDialog}
        loanId={loanId}
        onClose={closeEntityDialog}
        onDelete={handleDeleteFromEntityForm}
      />
      <EntityMemberFormDialog
        dialog={memberDialog}
        loanId={loanId}
        onClose={closeMemberDialog}
        onDelete={handleDeleteFromMemberForm}
      />
      <ConfirmationDialog
        open={!isNil(deleteTarget)}
        title={
          deleteTarget?.kind === "entity"
            ? "Delete Entity?"
            : "Remove Individual?"
        }
        message={
          deleteTarget?.kind === "entity"
            ? `Remove ${deleteTarget?.name} from this loan's ownership chart?${
                deleteTarget?.isBorrowingEntity
                  ? " This is the BORROWING entity — the loan will show no borrowing entity until you create a replacement."
                  : ""
              } This cannot be undone from the UI.`
            : `Remove ${deleteTarget?.name} from this entity? This cannot be undone from the UI.`
        }
        confirmText={deleteTarget?.kind === "entity" ? "Delete" : "Remove"}
        cancelText="Cancel"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteDialog}
      />
    </>
  );
};

export default LoanDrilldownEntityTab;

/* ============================================================
 * Data normalization — flat entities[] → tree
 * ============================================================
 * Post-migration shape: deal.entities[] is a flat array and ownership is
 * expressed via parentEntityId on each child pointing at the parent's _id.
 * No more deal.borrowerEntity field; no more nestedEntities[] sub-arrays.
 *
 *   root           = entities.find(e => e.borrowerEntity === true)
 *   childrenOf(id) = entities.filter(e => e.parentEntityId === id)
 *
 * The tree can branch (multiple owners per entity) — walk recursively.
 * ownershipPercentInParent on a child labels the edge up to its parent.
 */

const normalizeEntitiesArray = (entities, loanBorrowers) => {
  if (isNil(entities) || isEmpty(entities)) return null;
  const root = entities.find((e) => e?.borrowerEntity === true);
  if (!root) return null;

  const childrenOf = (parentId) =>
    entities.filter((e) => e?.parentEntityId === parentId);

  // Every entity that lands in the tree, so we can detect the ones that don't.
  const placed = new Set();

  const buildNode = (entity, ownershipPct, { unlinked = false } = {}) => {
    if (!entity) return null;
    if (entity._id) placed.add(entity._id);

    const members = (entity.entityMembers || [])
      .map((m) => normalizeMember(m, entity, loanBorrowers))
      .filter(Boolean);

    const nestedChildren = childrenOf(entity._id)
      .map((child) => buildNode(child, child?.ownershipPercentInParent))
      .filter(Boolean);

    return {
      id: entity._id || `ent-${entity.entityName || "unnamed"}`,
      type: "entity",
      entityId: entity._id,
      rawEntity: entity,
      entityName: entity.entityName || "Unnamed Entity",
      entityType: entity.entityType,
      entityEINNumber: entity.entityEINNumber,
      entityStateOfOrganization: entity.entityStateOfOrganization,
      entityAddress: entity.entityAddress || null,
      entityAddressText: addressLabel(entity.entityAddress) || null,
      isBorrowingEntity: Boolean(entity.borrowerEntity),
      // Not reachable from the root through parentEntityId — surfaced anyway
      // (see below) so its data isn't silently lost; flagged for the UI.
      isUnlinked: unlinked,
      parentEntityId: entity.parentEntityId,
      ownershipPct,
      members: [...members, ...nestedChildren],
    };
  };

  const tree = buildNode(root);

  // An entity written WITHOUT a parentEntityId (or a stray second
  // borrowerEntity) is attached to nothing, so the recursive walk above never
  // reaches it and it vanishes from the chart — which is how a whole entity
  // with members can silently disappear (e.g. the Opening Data Assistant
  // creating an unlinked entity). Attach each unreachable top-level orphan
  // under the root, flagged `isUnlinked`, so its data + members are visible and
  // the user can re-parent or delete it. Children of an orphan ride along via
  // buildNode's own recursion, so only the top of each stray subtree is added.
  const remaining = entities.filter((e) => e?._id && !placed.has(e._id));
  const remainingIds = new Set(remaining.map((e) => e._id));
  const topLevelOrphans = remaining.filter(
    (e) => !e.parentEntityId || !remainingIds.has(e.parentEntityId),
  );
  for (const orphan of topLevelOrphans) {
    const node = buildNode(orphan, orphan?.ownershipPercentInParent, {
      unlinked: true,
    });
    if (node) tree.members.push(node);
  }

  return tree;
};

const personFullName = (o) =>
  o?.fullName || [o?.firstName, o?.lastName].filter(Boolean).join(" ") || "";

/**
 * The borrower an entity member IS, if any.
 *
 * Entities ARE a first-class association target: `entities` is a top-level deal
 * array, so it works as both sides of POST /create_association — a borrower can
 * carry associations.entities and an entity can carry associations.borrowers
 * (the same shape the loan-document association flow writes to
 * `entities.$.associations`). What CANNOT be addressed is the member row:
 * entityMembers[] is nested inside entities[], and the endpoint's $elemMatch +
 * positional $set only reach top-level arrays. So the association proves
 * "this borrower belongs to this entity" and the name picks the member.
 *
 * Resolution order:
 *   1. Shared _id — the backend's own convention
 *      (BorrowingEntity.create_entity_member_from_borrower mints the member with
 *      the borrower's oid). How older rows are linked.
 *   2. The association, from either direction, narrowed to one member by name.
 *      PROVISIONAL: two members with the same name on one entity are ambiguous.
 *      An exact member-level link needs a borrower ref on EntityMember.
 */
const findLinkedBorrower = (member, parentEntity, loanBorrowers) => {
  if (!member || isEmpty(loanBorrowers)) return null;
  if (member._id) {
    const byId = loanBorrowers.find(
      (b) => b?._id && String(b._id) === String(member._id),
    );
    if (byId) return byId;
  }
  const entityId = parentEntity?._id ? String(parentEntity._id) : null;
  const memberName = personFullName(member).trim().toLowerCase();
  if (!entityId || !memberName) return null;
  const entityLinkedBorrowerIds = new Set(
    (parentEntity?.associations?.borrowers || []).map(String),
  );
  const isLinked = (b) =>
    entityLinkedBorrowerIds.has(String(b?._id)) ||
    (b?.associations?.entities || []).some((id) => String(id) === entityId);
  return (
    loanBorrowers.find(
      (b) =>
        isLinked(b) && personFullName(b).trim().toLowerCase() === memberName,
    ) || null
  );
};

/**
 * A borrower with the SAME first + last name as this member, used only to warn
 * that the two look like the same person while carrying no link between them.
 * Deliberately not a link: matching names is not identity.
 */
const findBorrowerByName = (member, loanBorrowers) => {
  const first = (member?.firstName || "").trim().toLowerCase();
  const last = (member?.lastName || "").trim().toLowerCase();
  if (!first || !last) return null;
  return (
    (loanBorrowers || []).find(
      (b) =>
        (b?.firstName || "").trim().toLowerCase() === first &&
        (b?.lastName || "").trim().toLowerCase() === last,
    ) || null
  );
};

const normalizeMember = (member, parentEntity, loanBorrowers) => {
  if (!member) return null;
  const matchedBorrower = findLinkedBorrower(member, parentEntity, loanBorrowers);
  // Same person by name, but nothing ties the two records together.
  const unlinkedNameMatch = matchedBorrower
    ? null
    : findBorrowerByName(member, loanBorrowers);
  const firstName = member.firstName ?? matchedBorrower?.firstName;
  const lastName = member.lastName ?? matchedBorrower?.lastName;
  const fullName =
    member.fullName ??
    matchedBorrower?.fullName ??
    [firstName, lastName].filter(Boolean).join(" ") ??
    null;

  const roles = Array.isArray(member.roles) ? member.roles.filter(Boolean) : [];

  return {
    id: member._id || `${parentEntity?._id}-${firstName}-${lastName}`,
    type: "individual",
    memberId: member._id,
    parentEntityId: parentEntity?._id,
    rawMember: member,
    firstName,
    middleName: member.middleName,
    lastName,
    fullName,
    ownershipPct: member.entityPercentOwnership,
    title: member.title,
    roles,
    isGuarantor: Boolean(member.isGuarantor),
    authorizedSignatory: Boolean(member.authorizedSignatory),
    linkedBorrowerId: matchedBorrower?._id || null,
    linkedBorrowerName: matchedBorrower
      ? personFullName(matchedBorrower) || null
      : null,
    unlinkedBorrowerName: unlinkedNameMatch
      ? personFullName(unlinkedNameMatch) || null
      : null,
  };
};

/* ============================================================
 * Percent formatting
 * ============================================================
 * Ownership values are stored as decimals (0.5 = 50%) — this matches
 * PercentInput/RffPercentField, which divide the user's input by 100
 * before saving. Display with one or two decimals, trim trailing zeros.
 */

const formatOwnershipPct = (value, { maxDecimals = 2 } = {}) => {
  if (isNil(value) || typeof value !== "number" || isNaN(value)) return null;
  const pct = value * 100;
  const rounded = Number(pct.toFixed(maxDecimals));
  return `${rounded}%`;
};

/**
 * Percent field with 0 / 25 / 50 / 75 / 100 shortcut buttons, an always-visible
 * percentage input (pinned to the button height), and a slider for fine-grained
 * selection. Clicking a preset sets the form field to the matching decimal
 * (0 / 0.25 / 0.5 / 0.75 / 1); typing in the input or dragging the slider sets
 * any value in between. All three write the same form field.
 */
const PERCENT_PRESETS = [0, 0.25, 0.5, 0.75, 1];

// Shared height so the preset buttons and the percent input line up.
const PRESET_CONTROL_H = 40;

const SLIDER_MARKS = PERCENT_PRESETS.map((p) => ({
  value: Math.round(p * 100),
  label: `${Math.round(p * 100)}%`,
}));

// One arrow click steps the ownership by 1 percentage point (0.01 as a decimal).
const OWNERSHIP_STEP = 0.01;
const clampPct = (v) => Math.max(0, Math.min(1, Math.round(v * 1e6) / 1e6));

const OwnershipStepChevron = ({ up }) => (
  <svg
    width="9"
    height="6"
    viewBox="0 0 12 8"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={up ? "M1 6.5 6 1.5 11 6.5" : "M1 1.5 6 6.5 11 1.5"} />
  </svg>
);

const stepBtnSx = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: 0,
  background: "#fafbfc",
  color: "#94a3b8",
  cursor: "pointer",
  padding: 0,
  transition: "background .12s, color .12s",
  "&:hover": { background: "#eff6ff", color: "#1d4ed8" },
  "&:active": { background: "#dbeafe" },
};

/**
 * Percent input with a `%` suffix and an up/down stepper column (each click
 * bumps by 1%). The form stores the decimal (0.5); this shows/edits the percent
 * (50). A local `raw` buffer owns the text while focused so a half-typed value
 * ("23.") isn't reformatted out from under the caret; blur hands display back
 * to the stored number.
 */
const OwnershipPercentInput = ({ value, onChange }) => {
  const isNum = typeof value === "number" && !isNaN(value);
  const pct = isNum ? Math.round(value * 100 * 1e6) / 1e6 : "";
  const [raw, setRaw] = useState(null);
  const shown = raw != null ? raw : pct === "" ? "" : String(pct);

  const bump = (dir) => {
    const base = isNum ? value : 0;
    setRaw(null);
    onChange(clampPct(base + dir * OWNERSHIP_STEP));
  };

  return (
    <Box sx={{ position: "relative", flex: 1, minWidth: 160 }}>
      <Box
        component="input"
        type="text"
        inputMode="decimal"
        value={shown}
        onChange={(e) => {
          const next = e.target.value;
          setRaw(next);
          const parsed = parseFloat(next.replace(/[^0-9.-]/g, ""));
          onChange(
            next.trim() === "" || isNaN(parsed) ? undefined : parsed / 100,
          );
        }}
        onBlur={() => setRaw(null)}
        sx={{
          width: "100%",
          height: PRESET_CONTROL_H,
          boxSizing: "border-box",
          padding: "0 56px 0 11px",
          border: "1px solid #cbd5e1",
          borderRadius: "8px",
          fontSize: 14,
          color: "#0f172a",
          outline: "none",
          appearance: "none",
          fontVariantNumeric: "tabular-nums",
          "&:hover": { borderColor: "#94a3b8" },
          "&:focus": {
            borderColor: "#3b82f6",
            boxShadow: "0 0 0 3px rgba(59,130,246,0.15)",
          },
        }}
      />
      {/* `%` suffix, sitting just left of the stepper column. */}
      <Box
        sx={{
          position: "absolute",
          right: 40,
          top: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          color: "#94a3b8",
          fontSize: 13,
          pointerEvents: "none",
        }}
      >
        %
      </Box>
      {/* Up/down stepper column pinned to the field's right edge. */}
      <Box
        sx={{
          position: "absolute",
          top: 1,
          right: 1,
          bottom: 1,
          width: 30,
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid #e2e8f0",
          borderRadius: "0 8px 8px 0",
          overflow: "hidden",
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Increase ownership"
          onClick={() => bump(1)}
          sx={stepBtnSx}
        >
          <OwnershipStepChevron up />
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Decrease ownership"
          onClick={() => bump(-1)}
          sx={{ ...stepBtnSx, borderTop: "1px solid #e2e8f0" }}
        >
          <OwnershipStepChevron />
        </Box>
      </Box>
    </Box>
  );
};

/* ============================================================
 * Layout — compute node (x,y) via depth-first subtree-width sweep
 * ============================================================ */

const NODE_W = 240;
// The borrowing entity's card grows only as wide as its own content needs, so a
// short name keeps the standard node width and a long one stays legible without
// opening the detail panel. Past the max the name truncates with a hover title.
const BORROWER_NODE_W_MAX = 440;
// ...but never narrower than this: the root card is the anchor of the chart and
// reads as an afterthought at the standard node width.
const BORROWER_NODE_W_MIN = 300;
const NODE_H = 100;
// Taller than a standard card: the borrower card is split by a rule into a
// header (name + type) and a detail block (address, EIN, chip). With neither an
// address nor an EIN there is no detail block — no rule, and no dead band above
// the chip — so the card collapses to the compact height.
const BORROWER_NODE_H = 128;
const BORROWER_NODE_H_COMPACT = 88;

// Does the borrower card have anything to show BELOW its header?
const hasEntityDetailRows = (node) =>
  Boolean(node?.entityAddressText || node?.entityEINNumber);
const H_GAP = 32;
const V_GAP = 72;

// The root (borrower / "Parent Entity") carries a bottom chip + EIN, so
// give it extra width AND height so nothing overlaps. Other nodes use the
// default NODE_W / NODE_H.
// Rough per-character advances for each SVG text row. The card is hand-drawn
// SVG, so there's no layout pass to measure against — these approximate the
// rendered widths closely enough to size the card and pick truncation lengths.
// Keep each in sync with the fontSize of the row it describes.
const NAME_CHAR_W = 6.6; // 13px / weight 600
const SUB_CHAR_W = 5.8; // 11px
const ADDR_CHAR_W = 5.0; // 9.5px
const EIN_CHAR_W = 6.0; // 10px monospace
const NAME_X = 56; // name + sublabel start (right of the icon)
const CARD_PAD = 16;
const CARD_RIGHT_PAD = 30; // clears the chevron affordance
const PARENT_CHIP_W = 100;

// Width the borrowing entity's card needs for its own content. Never narrower
// than a standard node, never wider than the max.
const measureBorrowerNodeWidth = (node) => {
  const name = node?.entityName || "";
  const sub = [node?.entityType, node?.entityStateOfOrganization]
    .filter(Boolean)
    .join(" · ");
  const ein = node?.entityEINNumber ? `EIN ${node.entityEINNumber}` : "";
  const content = Math.max(
    NAME_X + name.length * NAME_CHAR_W,
    NAME_X + sub.length * SUB_CHAR_W,
    CARD_PAD + ein.length * EIN_CHAR_W,
    CARD_PAD + PARENT_CHIP_W,
  );
  return Math.min(
    BORROWER_NODE_W_MAX,
    Math.max(BORROWER_NODE_W_MIN, Math.ceil(content + CARD_RIGHT_PAD)),
  );
};

const getNodeWidth = (node) =>
  node?.type === "entity" && node?.isBorrowingEntity
    ? measureBorrowerNodeWidth(node)
    : NODE_W;
const getNodeHeight = (node) =>
  node?.type === "entity" && node?.isBorrowingEntity
    ? hasEntityDetailRows(node)
      ? BORROWER_NODE_H
      : BORROWER_NODE_H_COMPACT
    : NODE_H;

const buildLayout = (root) => {
  const computeWidth = (node) => {
    const nodeW = getNodeWidth(node);
    if (!node.members || node.members.length === 0) {
      node._subtreeWidth = nodeW;
      return nodeW;
    }
    let total = 0;
    node.members.forEach((child, i) => {
      total += computeWidth(child);
      if (i < node.members.length - 1) total += H_GAP;
    });
    node._subtreeWidth = Math.max(nodeW, total);
    return node._subtreeWidth;
  };
  computeWidth(root);

  const nodes = [];
  const edges = [];
  const assign = (node, x, y, parent = null) => {
    const nodeW = getNodeWidth(node);
    const nodeH = getNodeHeight(node);
    const nodeX = x + (node._subtreeWidth - nodeW) / 2;
    const placed = { ...node, x: nodeX, y, w: nodeW, h: nodeH };
    nodes.push(placed);

    if (parent) {
      edges.push({
        from: { x: parent.x + parent.w / 2, y: parent.y + parent.h },
        to: { x: nodeX + nodeW / 2, y },
        pct: node.ownershipPct,
        fromId: parent.id,
        toId: node.id,
      });
    }

    if (node.members && node.members.length > 0) {
      let childX = x;
      const parentRef = { id: node.id, x: nodeX, y, w: nodeW, h: nodeH };
      node.members.forEach((child) => {
        assign(child, childX, y + nodeH + V_GAP, parentRef);
        childX += child._subtreeWidth + H_GAP;
      });
    }
  };
  assign(root, 0, 0);

  const width = nodes.reduce((max, n) => Math.max(max, n.x + n.w), 0);
  const height = nodes.reduce((max, n) => Math.max(max, n.y + n.h), 0);
  return { nodes, edges, width, height };
};

const findNode = (node, id) => {
  if (!node) return null;
  if (node.id === id) return node;
  if (node.members) {
    for (const m of node.members) {
      const found = findNode(m, id);
      if (found) return found;
    }
  }
  return null;
};

const collectStats = (root) => {
  let entities = 0;
  let individuals = 0;
  let guarantors = 0;
  // A "member" is any node that belongs to a parent — every individual plus
  // every nested entity. The root borrowing entity is not a member of anything,
  // so it's excluded (walked with isRoot=true).
  let members = 0;
  const walk = (n, isRoot) => {
    if (!n) return;
    if (n.type === "entity") entities += 1;
    else {
      individuals += 1;
      if (n.isGuarantor) guarantors += 1;
    }
    if (!isRoot) members += 1;
    (n.members || []).forEach((child) => walk(child, false));
  };
  walk(root, true);
  return { entities, individuals, guarantors, members };
};

/* ============================================================
 * Empty state
 * ============================================================ */

const EmptyState = ({ onCreate, needsMigration, loanId }) => {
  const dispatch = useDispatch();
  const { loading, migrateToFlatEntities } = useEntitiesHook();
  const [confirming, setConfirming] = useState(false);

  const handleMigrate = () => {
    migrateToFlatEntities({
      loanId,
      onSuccessFn: (response) => {
        // Response shape: { message, entities_count, MIGRATION_TODO_REMINDER, deal }
        // Surface the backend's dev-facing reminder in the console so
        // engineers notice the outstanding cross-codebase follow-ups.
        if (response?.MIGRATION_TODO_REMINDER) {
          console.warn(
            "[migrate_to_flat_entities]",
            response.MIGRATION_TODO_REMINDER,
          );
        }
        if (response?.deal) dispatch(loanDrilldownSet(response.deal));
        setConfirming(false);
      },
    });
  };

  return (
    <Flx column center gap={1.5} sx={{ p: 8, color: "#64748b" }}>
      <BusinessOutlined sx={{ fontSize: 40, opacity: 0.5 }} />
      <Htag h2 sx={{ color: "#334155" }}>
        {needsMigration
          ? "Entity data needs to be updated"
          : "No borrowing entity on this loan"}
      </Htag>
      <Txt sx={{ color: "#64748b", textAlign: "center", maxWidth: 460 }}>
        {needsMigration
          ? "The entity information on this loan is saved in an older format. Click below to update it so you can view and manage entities here."
          : "Add a borrowing entity to visualize ownership and members as an org chart."}
      </Txt>
      {needsMigration ? (
        confirming ? (
          <Flx column center gap={1} sx={{ mt: 1 }}>
            <Txt
              sx={{
                fontSize: 12,
                color: "#334155",
                textAlign: "center",
                maxWidth: 460,
              }}
            >
              Update the entity information for this loan? This only needs to
              happen once, and no data will be lost — everything already on the
              loan stays the same.
            </Txt>
            <Flx gap={1}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => setConfirming(false)}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleMigrate}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  background: "#0f172a",
                  color: "#ffffff",
                  "&:hover": { background: "#1e293b" },
                  px: 2,
                }}
              >
                {loading ? "Updating…" : "Yes, Update"}
              </Button>
            </Flx>
          </Flx>
        ) : (
          <Button
            size="small"
            onClick={() => setConfirming(true)}
            disabled={loading}
            sx={{
              mt: 1,
              textTransform: "none",
              background: "#0f172a",
              color: "#ffffff",
              "&:hover": { background: "#1e293b" },
              px: 2,
            }}
          >
            Update Entity Data
          </Button>
        )
      ) : (
        <Button
          size="small"
          startIcon={<AddOutlined />}
          onClick={onCreate}
          sx={{
            mt: 1,
            textTransform: "none",
            background: "#0f172a",
            color: "#ffffff",
            "&:hover": { background: "#1e293b" },
            px: 2,
          }}
        >
          Create Borrowing Entity
        </Button>
      )}
    </Flx>
  );
};

/* ============================================================
 * Chart shell
 * ============================================================ */

const EntityOrgChart = ({
  root,
  onAddNestedOnParent,
  onEditEntity,
  onAddMember,
  onEditMember,
  onDeleteEntity,
  onDeleteMember,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  // Which node's "add" stem button is showing (hover), which node's add menu is
  // open (click), and which kind of child the open menu is previewing.
  const [hoverAddId, setHoverAddId] = useState(null);
  const [addMenuNodeId, setAddMenuNodeId] = useState(null);
  const [ghostKind, setGhostKind] = useState(GHOST_KIND_INDIVIDUAL);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Left-rail tab switcher now drives the MAIN canvas, not the rail:
  // "Overview" shows the ownership org chart, "Documents" replaces it with the
  // entity documents view (per-docType upload cards + entity docs table).
  const [docView, setDocView] = useState(LEGEND_TAB_OVERVIEW);
  const isDocuments = docView === LEGEND_TAB_DOCUMENTS;

  const layout = useMemo(() => buildLayout(root), [root]);

  // Placed children per node, read off the edges (layout nodes carry no parent
  // ref). Used to park the ghost card to the right of the existing row, so the
  // preview never displaces the real tree.
  const childrenByParent = useMemo(() => {
    const byId = new Map(layout.nodes.map((n) => [n.id, n]));
    const map = new Map();
    for (const e of layout.edges) {
      const child = byId.get(e.toId);
      if (!child) continue;
      if (!map.has(e.fromId)) map.set(e.fromId, []);
      map.get(e.fromId).push(child);
    }
    return map;
  }, [layout]);

  const closeAddMenu = useCallback(() => {
    setAddMenuNodeId(null);
    setGhostKind(GHOST_KIND_INDIVIDUAL);
  }, []);
  const selected = useMemo(
    () => (selectedId ? findNode(root, selectedId) : null),
    [root, selectedId],
  );

  return (
    <Flx column sx={{ minHeight: 600, flexGrow: 1, overflow: "hidden" }}>
      <Flx
        fw
        sx={{
          flexGrow: 1,
          minHeight: 520,
          overflow: "hidden",
          maxHeight: "100vh",
        }}
      >
        {sidebarCollapsed ? (
          <CollapsedChartSidebar onExpand={() => setSidebarCollapsed(false)} />
        ) : (
          <Box
            sx={{
              width: 365,
              flexShrink: 0,
              borderRight: "1px solid #e3e8f0",
              background: "#ffffff",
              overflow: "auto",
              p: 2.5,
              transition: "width 0.18s ease",
            }}
          >
            <ChartHeader
              root={root}
              onCollapse={() => setSidebarCollapsed(true)}
            />
            <ChartLegendSide root={root} />
          </Box>
        )}
        <Flx fw sx={{ position: "relative", overflow: "hidden" }}>
          {isDocuments ? (
            <EntityDocumentsContent
              onExit={() => setDocView(LEGEND_TAB_OVERVIEW)}
            />
          ) : (
            <>
              {/* Canvas. position:relative so the hint + legend can float over
                  the chart instead of taking rail space; the dot grid is a
                  repeating radial-gradient, so it costs no markup and scales
                  with any chart size. */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexGrow: 1,
                  minWidth: 0,
                  height: "100%",
                  overflow: "hidden",
                  background: CANVAS_BG,
                  backgroundImage: `radial-gradient(${CANVAS_DOT} 1px, transparent 1px)`,
                  backgroundSize: `${CANVAS_DOT_SPACING}px ${CANVAS_DOT_SPACING}px`,
                }}
              >
              <Flx
                jc
                sx={{
                  px: 6,
                  pb: 6,
                  // Extra headroom so the top-centered hint pill never lands on
                  // the root card.
                  pt: 9,
                  width: layout.width + 200,
                  height: "100%",
                  flexGrow: 1,
                  overflow: "auto",
                }}
              >
                <svg
                  width={layout.width}
                  height={layout.height}
                  style={{ overflow: "visible" }}
                >
                  {layout.edges.map((edge, i) => (
                    <OrgEdge
                      key={`${edge.fromId}-${edge.toId}-${i}`}
                      edge={edge}
                      highlighted={
                        selectedId === edge.fromId || selectedId === edge.toId
                      }
                    />
                  ))}
                  {layout.nodes.map((node) => (
                    <OrgNode
                      key={node.id}
                      node={node}
                      selected={selectedId === node.id}
                      onClick={() => setSelectedId(node.id)}
                      // Hovering an entity card reveals the same add-below
                      // affordance as hovering the gap under it.
                      onHoverStart={
                        node.type === "entity"
                          ? () => setHoverAddId(node.id)
                          : undefined
                      }
                      onHoverEnd={
                        node.type === "entity"
                          ? () =>
                              setHoverAddId((cur) =>
                                cur === node.id ? null : cur,
                              )
                          : undefined
                      }
                    />
                  ))}
                  {/* Click-away target for the open add menu. Sits under the
                      affordances so the menu itself stays clickable. */}
                  {addMenuNodeId && (
                    <rect
                      x={-2000}
                      y={-2000}
                      width={layout.width + 4000}
                      height={layout.height + 4000}
                      fill="transparent"
                      style={{ pointerEvents: "all" }}
                      onClick={closeAddMenu}
                    />
                  )}
                  {/* Drawn last so the affordance sits above the cards and
                      edges it overlaps. */}
                  {layout.nodes
                    .filter((node) => node.type === "entity")
                    .map((node) => (
                      <NodeAddAffordance
                        key={`add-${node.id}`}
                        node={node}
                        kids={childrenByParent.get(node.id)}
                        visible={hoverAddId === node.id}
                        menuOpen={addMenuNodeId === node.id}
                        ghostKind={ghostKind}
                        onGhostKind={setGhostKind}
                        onToggleMenu={() =>
                          setAddMenuNodeId((cur) => {
                            if (cur === node.id) {
                              setGhostKind(GHOST_KIND_INDIVIDUAL);
                              return null;
                            }
                            setGhostKind(GHOST_KIND_INDIVIDUAL);
                            return node.id;
                          })
                        }
                        onEnter={() => setHoverAddId(node.id)}
                        onLeave={() =>
                          setHoverAddId((cur) =>
                            cur === node.id ? null : cur,
                          )
                        }
                        onAddIndividual={() => {
                          closeAddMenu();
                          onAddMember?.(node.entityId);
                        }}
                        onAddNested={() => {
                          closeAddMenu();
                          onAddNestedOnParent?.(node.entityId);
                        }}
                      />
                    ))}
                </svg>
              </Flx>

                {!selected && <CanvasHint />}
                <CanvasLegend />
              </Box>

              {selected ? (
                <Box
                  sx={{
                    width: 320,
                    flexShrink: 0,
                    borderLeft: "1px solid #e2e8f0",
                    background: "#ffffff",
                  }}
                >
                  <SelectedDetail
                    node={selected}
                    onClose={() => setSelectedId(null)}
                    onEditEntity={onEditEntity}
                    onAddMember={onAddMember}
                    onAddNestedOnParent={onAddNestedOnParent}
                    onEditMember={onEditMember}
                    onDeleteEntity={onDeleteEntity}
                    onDeleteMember={onDeleteMember}
                  />
                </Box>
              ) : null}
            </>
          )}
        </Flx>
      </Flx>
    </Flx>
  );
};

// Left-rail content. The eyebrow row is a tab switcher: "Overview" keeps the
// chart stats/legend/info card unchanged; "Documents" swaps in a compact
// upload + the list of all loan-level docs in the "Entity Docs" docGroup.
const LEGEND_TAB_OVERVIEW = "overview";
const LEGEND_TAB_DOCUMENTS = "documents";
const ENTITY_DOC_GROUP = "Entity Docs";
// Doc groups shown in the entity docs list/count — matches the entity upload
// `allowedDocGroups` so anything uploadable here is also viewable here.
const ENTITY_DOC_GROUPS = [ENTITY_DOC_GROUP, "Related Entity Docs"];

// The rail is a persistent info panel (stats + legend) regardless of the canvas
// mode; the Overview/Documents switcher that drives the main canvas lives up in
// ChartHeader, grouped with the entity name.
const ChartLegendSide = ({ root }) => {
  const stats = useMemo(() => collectStats(root), [root]);
  return (
    <>
      <ChartLegendOverview stats={stats} />
      <EntityDocsRailSection />
    </>
  );
};

// Entity documents listed in the rail, styled like the DSCR workbook's
// "Supporting files" section header, with the same grouped category cards the
// Documents sidebar uses (folder header + count pill + status-dot rows).
// The same DocGroup Coverage cards the Documents tab renders, one per entity doc
// group. Taxonomy-first beats a flat uploaded-files list here: an empty document
// type is the ask, and it is visible without opening anything — until the
// reviewer only wants what is on file, which is what the filter is for. Upload
// falls back to the global upload modal, since the rail sits outside the
// canvas's UploadQueueProvider.
const EntityDocsRailSection = () => {
  const [hideEmpty, setHideEmpty] = useState(false);
  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Flx ac jb g={1} sx={{ mb: 1.25 }}>
        <Txt sx={{ fontSize: 14.5, fontWeight: 600, color: "#0f172a" }}>
          Documents
        </Txt>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={hideEmpty}
              onChange={(e) => setHideEmpty(e.target.checked)}
              sx={{ p: 0.5 }}
            />
          }
          label="Hide empty documents"
          sx={{
            m: 0,
            flexShrink: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: 11.5,
              color: "#475569",
              ml: 0.5,
            },
          }}
        />
      </Flx>
      <Flx column g={1.25} sx={{ mx: "-8px" }}>
        {ENTITY_DOC_GROUPS.map((group) => (
          <DocGroupCoverageCard
            key={group}
            group={group}
            hideEmpty={hideEmpty}
          />
        ))}
      </Flx>
    </Box>
  );
};

const ChartLegendOverview = ({ stats }) => {
  return (
    <>
      <Flx column g={1.25} sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.25,
            mb: 3,
          }}
        >
          <StatBox
            label="Entities"
            value={stats.entities}
            icon={<BusinessOutlined sx={{ fontSize: 14 }} />}
          />
          <StatBox
            label="Individuals"
            value={stats.individuals}
            icon={<PersonOutlined sx={{ fontSize: 14 }} />}
          />
          <StatBox
            label="Guarantors"
            value={stats.guarantors}
            icon={<ShieldOutlined sx={{ fontSize: 14, color: "#059669" }} />}
          />
          <StatBox
            label="Members"
            value={stats.members}
            icon={<PeopleOutlined sx={{ fontSize: 14 }} />}
          />
        </Box>
      </Flx>

    </>
  );
};

/* Floating canvas chrome.
 * The legend and the "select a node" hint used to sit in the left rail, where
 * they pushed the stats and the docs list down. They are overlays on the chart
 * canvas now: hint top-right (only while nothing is selected), legend
 * bottom-right. Both are pointer-transparent so neither blocks a node click. */
const GHOST_KIND_INDIVIDUAL = "individual";
const GHOST_KIND_ENTITY = "entity";

const CANVAS_BG = "#f7f9fb";
const CANVAS_DOT = "#d5dee7";
const CANVAS_DOT_SPACING = 18;

// Shared look for everything floating over the dotted canvas. The dots make a
// low-contrast pill hard to read, so this is opaque white on a defined border
// with a real shadow rather than a hairline outline.
const FLOATING_CHROME_SX = {
  position: "absolute",
  zIndex: 2,
  background: "#ffffff",
  border: "1px solid #cbd5e1",
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.10)",
  pointerEvents: "none",
};


const CanvasHint = () => (
  <Flx
    ac
    g={1}
    sx={{
      ...FLOATING_CHROME_SX,
      top: 16,
      left: "50%",
      transform: "translateX(-50%)",
      borderRadius: "999px",
      px: 1.75,
      py: 0.85,
    }}
  >
    <InfoOutlined sx={{ fontSize: 15, color: "#64748b" }} />
    <Txt
      sx={{
        fontSize: 12.5,
        fontWeight: 500,
        color: "#0f172a",
        whiteSpace: "nowrap",
      }}
    >
      Select any node to view its details
    </Txt>
  </Flx>
);

/**
 * Add-below affordance. Hovering an entity card (or the strip under it) reveals
 * a single + anchored to the node's connector stem. Clicking it opens a menu of
 * the same two actions the detail rail offers; hovering a menu row previews the
 * new node as a ghost card parked to the RIGHT of the existing children, so the
 * preview never displaces the real tree. Mouse enter/leave live on the wrapping
 * <g> so moving from the card onto the menu does not count as leaving.
 */
const STEM_BTN_R = 13;
const ADD_MENU_W = 252;
const ADD_MENU_TOP = 20;

const NodeAddAffordance = ({
  node,
  kids,
  visible,
  menuOpen,
  ghostKind,
  onGhostKind,
  onToggleMenu,
  onEnter,
  onLeave,
  onAddIndividual,
  onAddNested,
}) => {
  const cx = node.x + node.w / 2;
  const stemY = node.y + node.h;
  const show = visible || menuOpen;

  // Where the new child would land: after the existing row, or centered under
  // the node when it has none. Drawn as an overlay, so nothing reflows.
  const ghostY = stemY + V_GAP;
  const ghostX =
    kids && kids.length > 0
      ? Math.max(...kids.map((k) => k.x + k.w)) + H_GAP
      : cx - NODE_W / 2;
  const ghostCx = ghostX + NODE_W / 2;
  const ghostMidY = (stemY + ghostY) / 2;

  // I / E while the menu is open, matching the hints on the rows.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      const k = e.key?.toLowerCase();
      if (k === "i") {
        e.preventDefault();
        onAddIndividual();
      } else if (k === "e") {
        e.preventDefault();
        onAddNested();
      } else if (e.key === "Escape") {
        onToggleMenu();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, onAddIndividual, onAddNested, onToggleMenu]);

  return (
    <g onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {/* Keeps the stem button reachable after the pointer leaves the card. */}
      <rect
        x={node.x}
        y={stemY}
        width={node.w}
        height={STEM_BTN_R * 2 + 6}
        fill="transparent"
        style={{ pointerEvents: "all" }}
      />

      {/* Ghost preview of the node about to be created. */}
      {menuOpen && (
        <g style={{ pointerEvents: "none" }}>
          <path
            d={`M ${cx} ${stemY} C ${cx} ${ghostMidY}, ${ghostCx} ${ghostMidY}, ${ghostCx} ${ghostY}`}
            fill="none"
            stroke="#93c5fd"
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />
          <rect
            x={ghostX}
            y={ghostY}
            width={NODE_W}
            height={NODE_H}
            rx={10}
            fill="#eff6ff"
            fillOpacity={0.75}
            stroke="#93c5fd"
            strokeWidth={1.5}
            strokeDasharray="6 5"
          />
          <text
            x={ghostCx}
            y={ghostY + NODE_H / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={13}
            fontWeight={600}
            fill="#1d4ed8"
          >
            {ghostKind === GHOST_KIND_ENTITY
              ? "+  New nested entity"
              : "+  New individual"}
          </text>
        </g>
      )}

      {/* The stem button. Always mounted so it can fade rather than snap. */}
      <g
        onClick={onToggleMenu}
        style={{
          opacity: show ? 1 : 0,
          transition: "opacity .2s ease",
          pointerEvents: show ? "auto" : "none",
          cursor: "pointer",
        }}
      >
        <circle
          cx={cx}
          cy={stemY}
          r={STEM_BTN_R}
          fill="#2563eb"
          stroke="#ffffff"
          strokeWidth={2}
        />
        <g transform={`translate(${cx}, ${stemY})`}>
          {/* One glyph: a + that rotates into an x while the menu is open. */}
          <g
            style={{
              transform: menuOpen ? "rotate(45deg)" : "rotate(0deg)",
              transition: "transform .18s ease",
            }}
          >
            <path
              d="M -5 0 H 5 M 0 -5 V 5"
              stroke="#ffffff"
              strokeWidth={2}
              strokeLinecap="round"
            />
          </g>
        </g>
      </g>

      {menuOpen && (
        <foreignObject
          x={cx - ADD_MENU_W / 2}
          y={stemY + ADD_MENU_TOP}
          width={ADD_MENU_W}
          height={200}
          style={{ overflow: "visible" }}
        >
          <Box
            sx={{
              width: ADD_MENU_W,
              p: 1,
              borderRadius: "12px",
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
            }}
          >
            <Txt
              sx={{
                display: "block",
                px: 1,
                pt: 0.5,
                pb: 1,
                fontSize: 11.5,
                color: "#94a3b8",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Add to {node.entityName}
            </Txt>
            <AddMenuRow
              icon={<PersonOutlined sx={{ fontSize: 16 }} />}
              title="Individual"
              description="A person — member, guarantor, or signer"
              hint="I"
              active={ghostKind === GHOST_KIND_INDIVIDUAL}
              onHover={() => onGhostKind(GHOST_KIND_INDIVIDUAL)}
              onClick={onAddIndividual}
            />
            <AddMenuRow
              icon={<BusinessOutlined sx={{ fontSize: 16 }} />}
              title="Nested entity"
              description="An LLC or corp that owns a stake"
              hint="E"
              active={ghostKind === GHOST_KIND_ENTITY}
              onHover={() => onGhostKind(GHOST_KIND_ENTITY)}
              onClick={onAddNested}
            />
          </Box>
        </foreignObject>
      )}
    </g>
  );
};

const AddMenuRow = ({
  icon,
  title,
  description,
  hint,
  active,
  onHover,
  onClick,
}) => (
  <Flx
    ac
    g={1.25}
    onMouseEnter={onHover}
    onClick={onClick}
    sx={{
      px: 1,
      py: 1,
      borderRadius: "8px",
      cursor: "pointer",
      background: active ? "#eff6ff" : "transparent",
      transition: "background .14s ease",
      "&:hover": { background: "#eff6ff" },
    }}
  >
    <Flx
      ac
      jc
      sx={{
        flexShrink: 0,
        width: 30,
        height: 30,
        borderRadius: "8px",
        background: active ? "#2563eb" : "#f1f5f9",
        color: active ? "#ffffff" : "#475569",
        transition: "background .14s ease, color .14s ease",
      }}
    >
      {icon}
    </Flx>
    <Flx column sx={{ flex: 1, minWidth: 0 }}>
      <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
        {title}
      </Txt>
      <Txt
        sx={{
          fontSize: 11.5,
          color: "#64748b",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {description}
      </Txt>
    </Flx>
    <Flx
      ac
      jc
      sx={{
        flexShrink: 0,
        minWidth: 18,
        height: 18,
        px: 0.5,
        borderRadius: "4px",
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        fontSize: 10.5,
        color: "#94a3b8",
      }}
    >
      {hint}
    </Flx>
  </Flx>
);

const CanvasLegend = () => (
  <Flx
    ac
    g={2}
    sx={{
      ...FLOATING_CHROME_SX,
      bottom: 16,
      left: 16,
      borderRadius: "10px",
      px: 1.75,
      py: 1,
    }}
  >
    <LegendSwatch
      compact
      color="#0f172a"
      borderColor="#0f172a"
      label="Borrowing entity"
    />
    <LegendSwatch
      compact
      color="#ffffff"
      borderColor="#cbd5e1"
      accentColor="#3b82f6"
      label="Parent entity"
    />
    <LegendSwatch
      compact
      color="#ffffff"
      borderColor="#c1cede"
      label="Individual"
    />
  </Flx>
);

// Full documents view rendered in the MAIN canvas (replaces the org chart when
// the "Documents" tab is active). It hosts a per-docType drop-card grid for
// every entity doc group — files land in a shared upload queue and are reviewed
// / committed from the bottom-docked FixedUploadQueue, exactly like the
// Documents tab's "Upload By Category" flow — followed by a table of every
// entity document on the loan.
const EntityDocumentsContent = ({ onExit }) => {
  return (
    <UploadQueueProvider>
      {/* The documents view follows the Drilldown Tab Header Standard: a page
          header + peer LoanSectionCards on the page background. It renders in the
          chart's main canvas (beside the info rail), so it reads as its own
          standardized mini-page when the "Documents" rail tab is active. */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          overflow: "auto",
          background: T.color.bg,
        }}
      >
        <EntityDocumentsUploadAndList onExit={onExit} />
      </Box>
      {/* Bottom-docked review/commit bar, fed by the drop cards above. */}
      <FixedUploadQueue />
    </UploadQueueProvider>
  );
};

// Per-doc-group label above each drop-card grid, on the standard field-label
// scale (uppercase, muted).
const DocGroupLabel = ({ children }) => (
  <Txt sx={{ ...T.type.fieldLabel, marginBottom: "8px" }}>{children}</Txt>
);

const EntityDocumentsUploadAndList = ({ onExit }) => {
  const { addedFiles, onFilesAdded, onRemoveFile } = useUploadQueue();
  const { getDocTypesByGroup } = useFileDocGroupAndTypesHook();
  const loanDocuments = useSelector(
    (state) => state.loanDrilldown?.loanDocuments,
  );
  const [search, setSearch] = useState("");

  const entityDocs = useMemo(() => {
    if (!loanDocuments) return [];
    return loanDocuments.filter(
      (d) => ENTITY_DOC_GROUPS.includes(d?.docGroup) && !d?.isHidden,
    );
  }, [loanDocuments]);

  // Alphabetize the entity doc groups A→Z, then drop any group with no
  // doc-type match for the active search so we never render an orphaned group
  // label above an empty grid.
  const visibleGroups = useMemo(() => {
    const sorted = [...ENTITY_DOC_GROUPS].sort((a, b) => a.localeCompare(b));
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (group) =>
        group.toLowerCase().includes(q) ||
        (getDocTypesByGroup(group) || []).some((dt) =>
          dt.toLowerCase().includes(q),
        ),
    );
  }, [search, getDocTypesByGroup]);

  return (
    <LoanTabShell
      maxWidth="lg"
      title="Entity documents"
      subTitle="Upload and review the documents tied to this loan's borrowing entity."
      // A sub-view toggled inside the tab, not a tab switch — don't yank focus.
      autoFocusTitle={false}
      endContent={
        onExit ? (
          <Flx ac g={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AccountTreeOutlined sx={{ fontSize: 15 }} />}
              onClick={onExit}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Return to Org Tree
            </Button>
            <Tooltip title="Close documents view" arrow>
              <IconButton size="small" onClick={onExit}>
                <CloseOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Flx>
        ) : undefined
      }
    >
      {/* Upload — one drop-card grid per entity doc group. */}
      <LoanSectionCard
        icon={<DescriptionOutlined />}
        title="Upload entity documents"
        subTitle="Drag files onto a document type, or click to browse. Queued files collect in the review bar at the bottom."
        controls={
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search document types"
            size="small"
            variant="filled"
            sx={{ width: 240 }}
          />
        }
      >
        {isEmpty(visibleGroups) ? (
          <Txt sx={{ ...T.type.meta }}>
            No document types match “{search}”.
          </Txt>
        ) : (
          <Flx column g={2.5}>
            {visibleGroups.map((group) => (
              <Box key={group}>
                <DocGroupLabel>{group}</DocGroupLabel>
                <CategoryDocTypeGrid
                  docGroup={group}
                  addedFiles={addedFiles}
                  onFilesAdded={onFilesAdded}
                  onRemoveFile={onRemoveFile}
                  search={search}
                  minCardWidth={220}
                />
              </Box>
            ))}
          </Flx>
        )}
      </LoanSectionCard>

      {/* All entity documents on the loan. */}
      <LoanSectionCard
        title="Entity documents"
        subTitle="Every document on this loan in an entity doc group."
        count={entityDocs.length > 0 ? `${entityDocs.length} total` : undefined}
        disableBodyPadding={!isEmpty(entityDocs)}
      >
        {isEmpty(entityDocs) ? (
          <SectionEmptyState
            icon={<DescriptionOutlined />}
            message="Upload an entity document above to get started."
          />
        ) : (
          <EntityFilesTable rowData={entityDocs} />
        )}
      </LoanSectionCard>
    </LoanTabShell>
  );
};

// Rail header: entity name + collapse control, with the Overview/Documents
// switcher directly beneath as a full-width segmented control. Grouping the
// switcher into the header (above the divider) keeps it from floating between
// the title and the stats.
const ChartHeader = ({ root, onCollapse }) => {
  return (
    <Box
      sx={{
        pb: 2,
        mb: 2,
        borderBottom: "1px solid #e2e8f0",
        background: "#ffffff",
      }}
    >
      <Flx jb sx={{ alignItems: "flex-start" }}>
        <Flx column>
          <Txt
            sx={{
              fontSize: 10,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#64748b",
              mb: 0.25,
            }}
          >
            Ownership Chart
          </Txt>
          <Htag
            h2
            sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", m: 0 }}
          >
            {root.entityName}
          </Htag>
        </Flx>
        {onCollapse && (
          <Tooltip title="Collapse sidebar" arrow>
            <IconButton
              size="small"
              onClick={onCollapse}
              sx={{
                p: 0.3,
                color: "#64748b",
                "&:hover": { background: "#f1f5f9", color: "#0f172a" },
              }}
              aria-label="Collapse Ownership Chart sidebar"
            >
              <ChevronLeftRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Flx>
    </Box>
  );
};

const CollapsedChartSidebar = ({ onExpand }) => {
  return (
    <Flx
      column
      ac
      sx={{
        width: 36,
        flexShrink: 0,
        borderRight: "1px solid #e3e8f0",
        background: "#ffffff",
        py: 1,
        gap: 1,
        transition: "width 0.18s ease",
      }}
    >
      <Tooltip title="Expand Ownership Chart" arrow placement="right">
        <IconButton
          size="small"
          onClick={onExpand}
          sx={{
            p: 0.3,
            color: "#64748b",
            "&:hover": { background: "#f1f5f9", color: "#0f172a" },
          }}
          aria-label="Expand Ownership Chart sidebar"
        >
          <ChevronRightRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Ownership Chart" arrow placement="right">
        <Box
          onClick={onExpand}
          sx={{
            cursor: "pointer",
            color: "#64748b",
            "&:hover": { color: "#0f172a" },
          }}
        >
          <AccountTreeOutlined sx={{ fontSize: 18 }} />
        </Box>
      </Tooltip>
      <Box
        onClick={onExpand}
        sx={{
          cursor: "pointer",
          mt: 1,
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#64748b",
          userSelect: "none",
          "&:hover": { color: "#0f172a" },
        }}
      >
        Ownership Chart
      </Box>
    </Flx>
  );
};

/* ============================================================
 * SVG pieces
 * ============================================================ */

const OrgEdge = ({ edge, highlighted }) => {
  const midY = (edge.from.y + edge.to.y) / 2;
  const midX = (edge.from.x + edge.to.x) / 2;
  const hasPct = !isNil(edge.pct);

  return (
    <g>
      <path
        d={`M ${edge.from.x} ${edge.from.y} C ${edge.from.x} ${midY}, ${edge.to.x} ${midY}, ${edge.to.x} ${edge.to.y}`}
        fill="none"
        stroke={highlighted ? "#0f172a" : "#cbd5e1"}
        strokeWidth={highlighted ? 2 : 1.5}
      />
      {hasPct && (
        <g transform={`translate(${midX}, ${midY})`}>
          <rect
            x={-24}
            y={-11}
            width={48}
            height={22}
            rx={11}
            fill="#ffffff"
            stroke="#cbd5e1"
            strokeWidth={1}
          />
          <text
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fontWeight={600}
            fill="#0f172a"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {formatOwnershipPct(edge.pct, { maxDecimals: 0 })}
          </text>
        </g>
      )}
    </g>
  );
};

const truncate = (str, max) => {
  if (!str) return "";
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

const OrgNode = ({ node, selected, onClick, onHoverStart, onHoverEnd }) => {
  const [hovered, setHovered] = useState(false);
  const isEntity = node.type === "entity";
  const isBorrower = isEntity && node.isBorrowingEntity;
  const label = isEntity
    ? node.entityName
    : node.fullName ||
      [node.firstName, node.lastName].filter(Boolean).join(" ");
  // Individual nodes: combine title + roles (deduped, title first) into a
  // single dot-separated sublabel so reviewers can see at a glance every
  // hat the member wears without opening the detail panel.
  const sublabel = isEntity
    ? [node.entityType, node.entityStateOfOrganization]
        .filter(Boolean)
        .join(" · ")
    : Array.from(
        new Set(
          [node.title, ...(Array.isArray(node.roles) ? node.roles : [])].filter(
            Boolean,
          ),
        ),
      ).join(" · ");

  // Hover lift. The root card is near-black and every other card is white, so
  // the two move in opposite directions: the dark one lightens toward slate-800,
  // the light ones settle onto the slate-50 the canvas already uses.
  const bg = isBorrower
    ? hovered
      ? "#1e293b"
      : "#0f172a"
    : hovered
      ? "#f8fafc"
      : "#ffffff";
  const textColor = isBorrower ? "#ffffff" : "#0f172a";
  const subColor = isBorrower ? "#94a3b8" : "#64748b";
  // Use a bright accent for the selected outline on dark backgrounds so the
  // selection state is visible against the borrower/parent-entity fill.
  const border = selected
    ? isBorrower
      ? "#60a5fa"
      : "#0f172a"
    : isEntity
      ? "#cbd5e1"
      : "#e2e8f0";
  const accent = isEntity && !isBorrower ? "#3b82f6" : null;

  // Ownership % pill geometry (top-right). Computed here so the name text can
  // be truncated to the space LEFT of the pill — keeping a gap between the name
  // and the % rather than letting a long name run under it.
  const pctText =
    !isBorrower && !isNil(node.ownershipPct)
      ? formatOwnershipPct(node.ownershipPct, { maxDecimals: 2 })
      : null;
  const pillW = pctText ? Math.max(34, pctText.length * 7 + 14) : 0;
  const pillLeftX = pctText ? node.w - 14 - pillW : node.w - 14;
  const NAME_GAP = 12; // clearance between the name and the pill / right edge
  // Cap each row to the space it actually has, so the name never reaches the
  // pill and the address never runs under the chevron.
  const nameMaxChars = Math.max(
    6,
    Math.floor((pillLeftX - NAME_X - NAME_GAP) / NAME_CHAR_W),
  );
  const subMaxChars = Math.max(
    6,
    Math.floor((pillLeftX - NAME_X - NAME_GAP) / SUB_CHAR_W),
  );
  const addrMaxChars = Math.max(
    10,
    Math.floor((node.w - CARD_PAD - CARD_RIGHT_PAD) / ADDR_CHAR_W),
  );
  // The borrower card separates its header (name + type) from its details
  // (address, EIN, chip) with a rule, so those rows sit lower than on a
  // standard card.
  const DIVIDER_Y = 56;
  const addrY = isBorrower ? 71 : 58;
  const einY = isBorrower ? 86 : 72;

  return (
    <g
      transform={`translate(${node.x}, ${node.y})`}
      onClick={onClick}
      onMouseEnter={() => {
        setHovered(true);
        onHoverStart?.();
      }}
      onMouseLeave={() => {
        setHovered(false);
        onHoverEnd?.();
      }}
      style={{ cursor: "pointer" }}
    >
      <rect
        x={2}
        y={3}
        width={node.w}
        height={node.h}
        rx={8}
        fill="#0f172a"
        opacity={hovered ? 0.1 : 0.05}
        style={{ transition: "opacity .16s ease" }}
      />
      <rect
        width={node.w}
        height={node.h}
        rx={8}
        fill={bg}
        stroke={border}
        strokeWidth={selected ? 2 : 1}
        style={{ transition: "fill .16s ease" }}
      />
      {accent && <rect width={4} height={node.h} rx={2} fill={accent} />}

      <g transform="translate(16, 16)">
        <rect
          width={28}
          height={28}
          rx={6}
          fill={isBorrower ? "#1e293b" : isEntity ? "#dbeafe" : "#f1f5f9"}
        />
        <foreignObject x={0} y={0} width={28} height={28}>
          <Box
            sx={{
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isEntity ? (
              <HouseOutlined
                sx={{
                  fontSize: 16,
                  color: isBorrower ? "#dbeafe" : "#1d4ed8",
                }}
              />
            ) : (
              <PersonOutlined
                sx={{
                  fontSize: 16,
                  color: "#475569",
                }}
              />
            )}
          </Box>
        </foreignObject>
      </g>

      {isBorrower && (
        <g transform={`translate(16, ${node.h - 32})`}>
          <rect width={100} height={18} rx={4} fill="#3b82f6" />
          <text
            x={50}
            y={9}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={9}
            fontWeight={700}
            fill="#ffffff"
            style={{ letterSpacing: 0.5 }}
          >
            PARENT ENTITY
          </text>
        </g>
      )}

      {/* Ownership % pill (top-right). The root/borrowing entity has no
          ownership-in-parent, so it's suppressed there; every other node
          shows the same value that labels its incoming edge, on the card
          itself so the split reads without tracing the connector. */}
      {pctText && (
        <g transform={`translate(${pillLeftX}, 13)`}>
          <rect
            width={pillW}
            height={20}
            rx={10}
            fill={isEntity ? "#eff6ff" : "#f1f5f9"}
            stroke={isEntity ? "#bfdbfe" : "#e2e8f0"}
            strokeWidth={1}
          />
          <text
            x={pillW / 2}
            y={10}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={10.5}
            fontWeight={700}
            fill={isEntity ? "#1d4ed8" : "#334155"}
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {pctText}
          </text>
        </g>
      )}

      <text x={NAME_X} y={28} fontSize={13} fontWeight={600} fill={textColor}>
        <title>{label}</title>
        {truncate(label, nameMaxChars)}
      </text>
      {sublabel && (
        <text x={NAME_X} y={45} fontSize={11} fill={subColor}>
          {truncate(sublabel, subMaxChars)}
        </text>
      )}

      {/* Divider between the borrower card's header and its detail rows —
          suppressed when there are no detail rows to separate. */}
      {isBorrower && hasEntityDetailRows(node) && (
        <line
          x1={CARD_PAD}
          y1={DIVIDER_Y}
          x2={node.w - CARD_PAD}
          y2={DIVIDER_Y}
          stroke="#ffffff"
          strokeOpacity={0.14}
          strokeWidth={1}
        />
      )}

      {/* Entity address (full address in the detail sidebar). Truncated here to
          fit the compact card; a <title> gives the full value on hover. */}
      {isEntity && node.entityAddressText && (
        <text x={CARD_PAD} y={addrY} fontSize={9.5} fill={subColor}>
          <title>{node.entityAddressText}</title>
          {truncate(node.entityAddressText, addrMaxChars)}
        </text>
      )}

      {isEntity && node.entityEINNumber && (
        <text
          x={CARD_PAD}
          y={einY}
          fontSize={10}
          fill={subColor}
          style={{ fontFamily: "ui-monospace, monospace" }}
        >
          EIN {node.entityEINNumber}
        </text>
      )}

      {/* Entity that isn't attached to the tree (no parentEntityId). Surfaced
          under the root so its data isn't lost; flagged so the user can
          re-parent or remove it. */}
      {isEntity && node.isUnlinked && (
        <g transform={`translate(16, ${node.h - 22})`}>
          <rect width={78} height={16} rx={3} fill="#fef3c7" />
          <text
            x={39}
            y={8}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={9}
            fontWeight={700}
            fill="#b45309"
          >
            UNLINKED
          </text>
        </g>
      )}

      {/* Subtle chevron — affordance that the card is clickable */}
      <path
        d={`M ${node.w - 18} ${node.h / 2 - 5} L ${node.w - 13} ${node.h / 2} L ${node.w - 18} ${node.h / 2 + 5}`}
        stroke={subColor}
        strokeWidth={1.5}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.7}
      />

      {!isEntity && node.isGuarantor && (
        <g transform={`translate(16, ${node.h - 22})`}>
          <rect width={76} height={16} rx={3} fill="#d1fae5" />
          <text
            x={38}
            y={8}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={9}
            fontWeight={600}
            fill="#047857"
          >
            GUARANTOR
          </text>
        </g>
      )}

      {!isEntity && node.authorizedSignatory && (
        <g
          transform={`translate(${node.isGuarantor ? 98 : 16}, ${node.h - 22})`}
        >
          <rect width={56} height={16} rx={3} fill="#dbeafe" />
          <text
            x={28}
            y={8}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={9}
            fontWeight={600}
            fill="#1d4ed8"
          >
            SIGNER
          </text>
        </g>
      )}
    </g>
  );
};

/* ============================================================
 * Right-panel — selection details
 * ============================================================ */

const DetailField = ({ label, value, mono }) => (
  <Flx column sx={{ mb: 2 }}>
    <Txt
      sx={{
        fontSize: 11,
        color: "#64748b",
        mb: 0.25,
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontSize: 13,
        color: "#0f172a",
        fontFamily: mono ? "ui-monospace, monospace" : undefined,
      }}
    >
      {isNil(value) || value === "" ? "—" : value}
    </Txt>
  </Flx>
);

/**
 * Shown when an entity member shares a name with a borrower on the loan but
 * carries no link to it. The two are separate documents: editing one does not
 * touch the other.
 */
const NotLinkedNotice = ({ name }) => (
  <Flx
    g={1}
    sx={{
      mt: 0.5,
      px: 1.5,
      py: 1.25,
      borderRadius: "8px",
      background: "#fffbeb",
      border: "1px solid #fde68a",
      alignItems: "flex-start",
    }}
  >
    <InfoOutlined sx={{ fontSize: 15, color: "#b45309", mt: "1px" }} />
    <Flx column g={0.5}>
      <Txt sx={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>
        Not linked to {name}
      </Txt>
      <Txt sx={{ fontSize: 11.5, color: "#92400e", lineHeight: 1.45 }}>
        An applicant on this loan has the same name, but the two records are not
        connected — updating a value here will not update it there, or the other
        way round. Linking is coming soon.
      </Txt>
    </Flx>
  </Flx>
);

const SelectedDetail = ({
  node,
  onClose,
  onEditEntity,
  onAddMember,
  onAddNestedOnParent,
  onEditMember,
  onDeleteEntity,
  onDeleteMember,
}) => {
  const isEntity = node.type === "entity";
  const title = isEntity
    ? node.entityName
    : node.fullName ||
      [node.firstName, node.lastName].filter(Boolean).join(" ");

  const handleEdit = () => {
    if (isEntity) {
      onEditEntity?.(node.rawEntity);
    } else {
      onEditMember?.(node.parentEntityId, node.rawMember);
    }
  };

  const handleAddIndividual = () => {
    if (isEntity) onAddMember?.(node.entityId);
  };

  const handleAddNestedEntity = () => {
    if (isEntity) onAddNestedOnParent?.(node.entityId);
  };

  // The 409 guard, mirrored client-side: an entity that still owns other
  // entities can't be deleted, so disable rather than let the call fail.
  // node.members mixes individuals and nested entity nodes — the entity
  // ones are exactly the children holding parentEntityId === this entity.
  const childEntities = isEntity
    ? (node.members || []).filter((m) => m?.type === "entity")
    : [];
  const deleteBlockedBy = childEntities
    .map((c) => c.entityName)
    .filter(Boolean);

  const handleDelete = () => {
    if (isEntity) {
      if (deleteBlockedBy.length > 0) return;
      onDeleteEntity?.(node);
    } else {
      onDeleteMember?.(node.parentEntityId, node);
    }
  };

  return (
    <Box>
      <Box sx={{ p: 2.5, borderBottom: "1px solid #f1f5f9" }}>
        <Flx jb sx={{ alignItems: "flex-start" }}>
          <Flx column sx={{ flex: 1, minWidth: 0 }}>
            <Txt
              sx={{
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#64748b",
                mb: 0.25,
              }}
            >
              {isEntity ? "Entity" : "Individual"} Details
            </Txt>
            <Txt sx={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>
              {title}
            </Txt>
          </Flx>
          <IconButton size="small" onClick={onClose}>
            <CloseOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </Flx>
        {/* Edit sits with the name it edits, not down in the add/delete stack —
            it acts on THIS record, the buttons below create others. A sibling of
            the title row, not a child of it, so it spans the whole panel. */}
        <Button
          size="small"
          fullWidth
          startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
          onClick={handleEdit}
          variant="outlined"
          sx={{
            mt: 1.5,
            textTransform: "none",
          }}
        >
          Edit {isEntity ? "Entity" : "Individual"}
        </Button>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {isEntity ? (
          <>
            <DetailField label="Legal Name" value={node.entityName} />
            <DetailField label="Type" value={node.entityType} />
            <DetailField label="Address" value={node.entityAddressText} />
            <DetailField label="EIN" value={node.entityEINNumber} mono />
            <DetailField
              label="State of Organization"
              value={node.entityStateOfOrganization}
            />
            <DetailField
              label="Borrowing Entity"
              value={node.isBorrowingEntity ? "Yes" : "No"}
            />
            {!isNil(node.ownershipPct) && (
              <DetailField
                label="Ownership in Parent"
                value={formatOwnershipPct(node.ownershipPct)}
              />
            )}
            <DetailField
              label="Direct Members"
              value={
                (node.members || []).filter((m) => m.type !== "entity").length
              }
            />
          </>
        ) : (
          <>
            <DetailField label="Name" value={title} />
            <DetailField label="Title" value={node.title} />
            <DetailField
              label="Additional Roles"
              value={
                Array.isArray(node.roles) && node.roles.length > 0
                  ? node.roles.join(", ")
                  : null
              }
            />
            <DetailField
              label="Ownership %"
              value={formatOwnershipPct(node.ownershipPct)}
            />
            <DetailField
              label="Guarantor"
              value={node.isGuarantor ? "Yes" : "No"}
            />
            <DetailField
              label="Authorized Signatory"
              value={node.authorizedSignatory ? "Yes" : "No"}
            />
            {node.linkedBorrowerId && (
              <DetailField
                label="Linked Borrower"
                value={`${node.linkedBorrowerName || "This borrower"} — edits here also update the borrower record`}
              />
            )}
            {node.unlinkedBorrowerName && (
              <NotLinkedNotice name={node.unlinkedBorrowerName} />
            )}
          </>
        )}
      </Box>

      <Divider />
      <Flx column gap={1} sx={{ p: 2.5 }}>
        {isEntity && (
          <>
            <Button
              size="small"
              startIcon={<PersonAddAltOutlined sx={{ fontSize: 14 }} />}
              onClick={handleAddIndividual}
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                background: "#0f172a",
                color: "#ffffff",
                "&:hover": { background: "#1e293b" },
              }}
            >
              Add Individual
            </Button>
            <Button
              size="small"
              startIcon={<BusinessOutlined sx={{ fontSize: 14 }} />}
              onClick={handleAddNestedEntity}
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                background: "#0f172a",
                color: "#ffffff",
                "&:hover": { background: "#1e293b" },
              }}
            >
              Add Nested Entity
            </Button>
          </>
        )}
        <Tooltip
          title={
            deleteBlockedBy.length > 0
              ? `Owned by this entity: ${deleteBlockedBy.join(
                  ", ",
                )}. Delete ${
                  deleteBlockedBy.length === 1 ? "it" : "them"
                } first — ownership is cleared leaf-up.`
              : ""
          }
          placement="top"
        >
          {/* span: a disabled MUI Button swallows the events Tooltip needs */}
          <span>
            <Button
              fullWidth
              size="small"
              color="error"
              variant="outlined"
              disabled={deleteBlockedBy.length > 0}
              startIcon={<DeleteOutlineOutlined sx={{ fontSize: 14 }} />}
              onClick={handleDelete}
              sx={{ textTransform: "none", justifyContent: "flex-start" }}
            >
              {isEntity ? "Delete Entity" : "Remove Individual"}
            </Button>
          </span>
        </Tooltip>
      </Flx>
    </Box>
  );
};

const StatBox = ({ label, value, icon }) => (
  <Box
    sx={{
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      p: 1.5,
    }}
  >
    <Flx ac gap={0.75} sx={{ color: "#64748b", mb: 0.5 }}>
      {icon}
      <Txt sx={{ fontSize: 11, color: "#64748b" }}>{label}</Txt>
    </Flx>
    <Txt
      sx={{
        fontSize: 18,
        fontWeight: 600,
        color: "#0f172a",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Txt>
  </Box>
);

const LegendSwatch = ({ color, borderColor, accentColor, label, compact }) => (
  <Flx ac gap={compact ? 0.85 : 1.25}>
    <Box
      sx={{
        width: compact ? 12 : 24,
        height: compact ? 12 : 24,
        borderRadius: compact ? "3px" : "4px",
        background: color,
        border: `1px solid ${borderColor}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {accentColor && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: compact ? 3 : 4,
            background: accentColor,
          }}
        />
      )}
    </Box>
    <Txt
      sx={{
        fontSize: compact ? 11.5 : 13,
        fontWeight: compact ? 500 : 400,
        color: compact ? "#0f172a" : "#334155",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Txt>
  </Flx>
);

/* ============================================================
 * Entity add/edit dialog — wired to useEntitiesHook
 * ============================================================ */

// Both entity edit panels dock at the same width — a little wider than the
// shell default so the address field and ownership presets are not cramped.
const ENTITY_SIDEBAR_WIDTH = 680;

/**
 * Ownership % with presets, CONTROLLED — the panel-form twin of
 * `RffPercentFieldWithPresets`. Same three affordances (typed percent,
 * quarter presets, slider) and the same stored contract: a FRACTION, 0.25
 * for 25%.
 */
const PercentWithPresets = ({ label, helperText, span, value, onChange }) => {
  const isNum = typeof value === "number" && !isNaN(value);
  const sliderValue = isNum ? Math.round(value * 100) : 0;
  return (
    <FormField label={label} helperText={helperText} span={span}>
      <Flx column gap={1.5}>
        <Flx gap={0.75} ac sx={{ flexWrap: "wrap" }}>
          <OwnershipPercentInput value={value} onChange={onChange} />
          <Box
            sx={{
              width: "1px",
              height: PRESET_CONTROL_H,
              bgcolor: "#e2e8f0",
              alignSelf: "center",
              mx: 0.25,
            }}
          />
          {PERCENT_PRESETS.map((preset) => (
            <Button
              key={preset}
              size="medium"
              variant={value === preset ? "contained" : "outlined"}
              onClick={() => onChange(preset)}
              sx={{
                minWidth: 56,
                height: PRESET_CONTROL_H,
                textTransform: "none",
              }}
            >
              {Math.round(preset * 100)}%
            </Button>
          ))}
        </Flx>
        <Box sx={{ px: 1.5, pt: 0.25 }}>
          <Slider
            size="small"
            value={sliderValue}
            onChange={(_e, v) => typeof v === "number" && onChange(v / 100)}
            min={0}
            max={100}
            step={1}
            marks={SLIDER_MARKS}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
        </Box>
      </Flx>
    </FormField>
  );
};

/**
 * The entity form's sections. A NESTED entity owns a share of the entity
 * below it and has a type of its own; the BORROWING entity is the one on the
 * note, so neither question applies to it — `visibleWhen` reads the
 * `borrowerEntity` flag the dialog seeds.
 */
const entitySections = (values) => [
  {
    key: "entity",
    title: "Entity",
    helper: "The name on the note, and where it sits in the chain.",
    collapseWhen: SCOPE.ALL,
    fields: [
      {
        name: "entityName",
        label: "Entity name",
        // Name and type share a row, two thirds / one third. The BORROWING
        // entity has no type question, so there the name takes the row
        // rather than leaving a third of it empty.
        span: values?.borrowerEntity ? 6 : 4,
        req: true,
        placeholder: "e.g. Hudson Holdings LLC",
      },
      {
        name: "entityType",
        label: "Entity type",
        kind: "combo",
        span: 2,
        options: selectOptionsEntityType,
        visibleWhen: (ctx) => !ctx.values?.borrowerEntity,
      },
      {
        name: "ownershipPercentInParent",
        label: "Ownership % in entity below",
        // Full width: the control is a typed percent, five preset buttons and
        // a marked slider — half a row wraps the presets onto a second line.
        span: 6,
        helperText:
          "Portion of the entity directly below this one in the chain that this entity owns.",
        kind: "percentPresets",
        visibleWhen: (ctx) => !ctx.values?.borrowerEntity,
        render: ({ field, value, onChange }) => (
          <PercentWithPresets
            label={field.label}
            helperText={field.helperText}
            span={field.span}
            value={value}
            onChange={onChange}
          />
        ),
      },
      {
        name: "entityAddress",
        label: "Entity address",
        kind: "googleAddress",
        span: 6,
        allowManualEntry: true,
        helperText:
          "The principal or registered address. A registered-agent address often won't resolve in Places — type it in by hand if so.",
      },
    ],
  },
  {
    key: "registration",
    title: "Registration",
    helper: "How and where the entity is filed.",
    collapseWhen: SCOPE.ALL,
    fields: [
      {
        name: "entityStateOfOrganization",
        label: "State of organization",
        kind: "combo",
        span: 3,
        options: selectOptionsUsaStates,
      },
      { name: "entityEINNumber", label: "EIN", span: 3, placeholder: "12-3456789" },
      {
        name: "entityOrganizationDate",
        label: "Organization date",
        kind: "date",
        span: 3,
      },
      {
        name: "entityGoodstandingDate",
        label: "Good standing through",
        kind: "date",
        span: 3,
        helperText: "The date the certificate of good standing runs to.",
      },
    ],
  },
];

const EntityFormDialog = ({ dialog, loanId, onClose, onDelete }) => {
  const dispatch = useDispatch();
  const {
    loading,
    createLoanEntity,
    updateEntityData,
    createNestedEntityOnParent,
  } = useEntitiesHook();

  const mode = dialog?.mode;
  const entity = dialog?.entity;
  const parentEntityId = dialog?.parentEntityId;
  const isEdit = mode === "edit";
  const isNested = mode === "create-nested";
  const isNestedOnParent = mode === "create-nested-on-parent";
  const isBorrower = mode === "create-borrower";

  const title = isEdit
    ? "Edit Entity"
    : isNestedOnParent
      ? "Add Nested Entity"
      : isNested
        ? "Add Parent Entity"
        : "Create Borrowing Entity";

  // Keyed on the dialog itself so every open starts clean. Keyed on
  // mode/entity it did not reset between a closed panel and "Add Parent
  // Entity" (both read as not-edit, not-borrower, no entity), so a percent
  // typed in one session was still in the working copy the next time.
  const initialValues = useMemo(() => {
    if (isEdit) {
      return { ...entity, borrowerEntity: Boolean(entity?.borrowerEntity) };
    }
    return { borrowerEntity: isBorrower };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog]);

  const [values, setValues] = useState(initialValues);
  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const setValue = useCallback(
    (name, v) => setValues((prev) => ({ ...prev, [name]: v })),
    [],
  );

  const sections = useMemo(() => entitySections(values), [values]);
  const ctx = useMemo(() => ({ values }), [values]);
  const required = useMemo(() => requiredOf(sections, ctx), [sections, ctx]);
  const requiredFilled = countRequiredFilled(required, values);
  // An edit sends only the fields this panel shows, and only the ones that
  // changed. The working copy holds the whole stored entity (liquidity, SF
  // ids, members…) — none of that is the panel's to write back.
  const editChanges = useMemo(() => {
    if (!isEdit) return {};
    const shown = new Set(
      sections.flatMap((s) =>
        (s.fields || [])
          .filter((f) => !f.visibleWhen || f.visibleWhen(ctx))
          .map((f) => f.name),
      ),
    );
    return Object.fromEntries(
      Object.entries(changedOnly(values, initialValues)).filter(([k]) =>
        shown.has(k),
      ),
    );
  }, [isEdit, sections, ctx, values, initialValues]);
  const hasChanges = isEdit ? Object.keys(editChanges).length > 0 : true;
  const canSave = requiredFilled === required.length && hasChanges;

  const onSubmit = useCallback(() => {
    if (!canSave) return;
    if (isEdit) {
      // No borrowerEntity / nestedEntity here: an edit never moves the entity
      // in the chain, so restating them only rewrote what was already stored.
      updateEntityData({
        loanId,
        entityId: entity?._id,
        data: editChanges,
        onSuccessFn: (response) => {
          dispatch(loanDrilldownSet(response));
          onClose();
        },
      });
      return;
    }
    const isBorrowerEntity = Boolean(values?.borrowerEntity);
    // `_id` is stripped: it is minted server-side, and a stray one makes the
    // backend's write try to set an immutable `_id`, which aborts it.
    const payload = {
      ...stripKeys(prune(values)),
      borrowerEntity: isBorrowerEntity,
      nestedEntity: !isBorrowerEntity,
    };
    if (isNestedOnParent) {
      // STUB: backend endpoint not yet implemented — the hook just logs.
      createNestedEntityOnParent({
        loanId,
        parentEntityId,
        data: payload,
        onSuccessFn: (response) => {
          if (response) dispatch(loanDrilldownSet(response));
          onClose();
        },
      });
      return;
    }
    createLoanEntity({
      loanId,
      data: payload,
      onSuccessFn: (response) => {
        dispatch(loanDrilldownSet(response));
        onClose();
      },
    });
  }, [
    canSave,
    values,
    editChanges,
    isEdit,
    isNestedOnParent,
    entity?._id,
    parentEntityId,
    loanId,
    createLoanEntity,
    createNestedEntityOnParent,
    updateEntityData,
    dispatch,
    onClose,
  ]);

  return (
    <ResizeableSidebar
      open={!!dialog}
      onClose={onClose}
      title={title}
      hideBackdrop
      titleIcon={
        <Box sx={formSx.panelHeadIcon}>
          <BusinessOutlined />
        </Box>
      }
      defaultWidth={ENTITY_SIDEBAR_WIDTH}
      sx={formSx.panelContainer}
      headerSx={formSx.panelHead}
      bodyPadding={0}
      bodySx={formSx.panelBody}
      headerEndContent={
        isEdit && onDelete ? (
          <Tooltip title="Delete Entity">
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteOutlineOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : null
      }
      footerContent={
        <FormPanelFooter
          required={required.length}
          filled={requiredFilled}
          readyText={
            isEdit ? (hasChanges ? "Ready to save" : "No changes yet") : "Ready to add"
          }
        >
          <Button
            variant="outlined"
            color="gray"
            sx={formSx.button}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            sx={formSx.button}
            disabled={!canSave || loading}
            onClick={onSubmit}
          >
            {isEdit ? "Save changes" : "Add entity"}
          </Button>
        </FormPanelFooter>
      }
    >
      {dialog && (
        <Box>
          {/* Pull an existing entity from Salesforce (any create flow). Fills
              the name + EIN and carries the SF ids so the entity is linked. */}
          {!isEdit && (
            <Box sx={formSx.panelSection}>
              <SalesforceEntitySearchBase
                sx={{ mb: 0 }}
                onSelect={(mapped) =>
                  setValues((prev) => ({ ...prev, ...mapped }))
                }
              />
            </Box>
          )}
          <PanelSections sections={sections} ctx={ctx} setValue={setValue} />
        </Box>
      )}
    </ResizeableSidebar>
  );
};

/* ============================================================
 * Entity member add/edit dialog — wired to useEntitiesHook
 * ============================================================ */

const MEMBER_ADD_TYPES = {
  NEW: "Create New",
  IMPORT: "Import Borrower",
};

// Fields an EntityMember and a Borrower both carry. Title / roles / authorized
// signatory are member-only and have no borrower counterpart, so they never
// mirror.
const BORROWER_MIRRORED_FIELDS = [
  "firstName",
  "middleName",
  "lastName",
  "fullName",
  "entityPercentOwnership",
  "isGuarantor",
];

const pickBorrowerMirroredFields = (payload) => {
  const out = {};
  for (const key of BORROWER_MIRRORED_FIELDS) {
    if (payload && key in payload) out[key] = payload[key];
  }
  return out;
};

/**
 * Borrower → EntityMember form values.
 *
 * The borrower's _id is deliberately NOT carried over: the member is its own
 * document and the backend mints it a fresh _id. The link is recorded after the
 * create, as an association on the borrower (see handleImportedAssociation).
 * Title / roles / authorized signatory have no borrower source and are left for
 * the user to fill in.
 */
const mapBorrowerToMemberValues = (borrower) => ({
  firstName: borrower?.firstName || "",
  middleName: borrower?.middleName || "",
  lastName: borrower?.lastName || "",
  fullName:
    borrower?.fullName ||
    [borrower?.firstName, borrower?.lastName].filter(Boolean).join(" "),
  entityPercentOwnership: borrower?.entityPercentOwnership ?? 0,
  isGuarantor: Boolean(borrower?.isGuarantor),
});

const borrowerDisplayName = (b) => personFullName(b) || "Unnamed borrower";

// Picking a row seeds the Create New form rather than posting straight away, so
// the user can set the title/roles the borrower record has no value for.
const BorrowerImportList = ({ borrowers, onSelect }) => {
  if (!borrowers || borrowers.length === 0) {
    return (
      <EmptyStatePlaceholder
        icon={<PeopleOutlined />}
        title="No borrowers left to import"
        description="Every borrower on this loan is already a member of this entity. Use Create New to add someone else."
        py={5}
      />
    );
  }
  return (
    <Flx column g={1}>
      {borrowers.map((b) => (
        <Flx
          key={b._id}
          ac
          jb
          onClick={() => onSelect(b)}
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            cursor: "pointer",
            "&:hover": { borderColor: "#94a3b8", background: "#f8fafc" },
          }}
        >
          <Flx column>
            <Txt sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>
              {borrowerDisplayName(b)}
            </Txt>
            <Txt sx={{ fontSize: 12, color: "#64748b" }}>
              {[
                !isNil(b?.entityPercentOwnership)
                  ? `${formatOwnershipPct(b.entityPercentOwnership)} ownership`
                  : null,
                b?.isGuarantor ? "Guarantor" : null,
                b?.emailAddress || null,
              ]
                .filter(Boolean)
                .join(" · ") || "No ownership or contact details on file"}
            </Txt>
          </Flx>
          <ChevronRightRounded sx={{ fontSize: 18, color: "#94a3b8" }} />
        </Flx>
      ))}
    </Flx>
  );
};

/**
 * The member form's sections. Title and roles read from the same option list
 * on purpose: `roles` is the multi-select the model is moving to, `title` the
 * single legacy field the chart still prints.
 */
const memberSections = () => [
  {
    key: "person",
    title: "Individual",
    helper: "Who this member is.",
    collapseWhen: SCOPE.ALL,
    fields: [
      { name: "firstName", label: "First name", span: 2, req: true },
      { name: "middleName", label: "Middle name", span: 2 },
      { name: "lastName", label: "Last name", span: 2, req: true },
    ],
  },
  {
    key: "role",
    title: "Role & ownership",
    helper: "What they do for the entity, and how much of it they own.",
    collapseWhen: SCOPE.ALL,
    fields: [
      {
        name: "title",
        label: "Title",
        kind: "chips",
        span: 6,
        options: selectOptionsEntityMemberTitle,
      },
      {
        name: "roles",
        label: "Additional roles",
        kind: "chipsMulti",
        span: 6,
        optional: true,
        options: selectOptionsEntityMemberTitle,
      },
      {
        name: "entityPercentOwnership",
        label: "Ownership %",
        span: 6,
        kind: "percentPresets",
        render: ({ field, value, onChange }) => (
          <PercentWithPresets
            label={field.label}
            span={field.span}
            value={value}
            onChange={onChange}
          />
        ),
      },
    ],
    toggles: [
      {
        name: "isGuarantor",
        title: "Is a loan guarantor",
        subtext: "This member is a guarantor on this loan",
      },
      {
        name: "authorizedSignatory",
        title: "Authorized signatory",
        subtext: "Can sign loan documents on behalf of the entity",
      },
    ],
  },
];

const EntityMemberFormDialog = ({ dialog, loanId, onClose, onDelete }) => {
  const dispatch = useDispatch();
  const { loading, createLoanEntityMember, updateLoanEntityMember } =
    useEntitiesHook();
  const { updateBorrower } = useUnderwritingHook();

  const mode = dialog?.mode;
  const member = dialog?.member;
  const entityId = dialog?.entityId;
  const isEdit = mode === "edit";

  // Create flow only: Create New (default) or seed the form from one of the
  // loan's existing borrowers — same chooser shape as Add Loan Related Contact.
  const [addType, setAddType] = useState(MEMBER_ADD_TYPES.NEW);
  const [importedFrom, setImportedFrom] = useState(null);

  const borrowers = useSelector((state) => state.loanDrilldown?.borrowers);
  const entities = useSelector((state) => state.loanDrilldown?.entities);

  const parentEntity = useMemo(
    () => (entities || []).find((e) => e?._id === entityId) || null,
    [entities, entityId],
  );

  // Borrowers not already on this entity — an already-imported borrower would
  // be a duplicate, not a second person.
  const importableBorrowers = useMemo(() => {
    if (isEdit) return [];
    const taken = new Set(
      (parentEntity?.entityMembers || [])
        .map((m) => findLinkedBorrower(m, parentEntity, borrowers)?._id)
        .filter(Boolean)
        .map(String),
    );
    return (borrowers || []).filter((b) => b?._id && !taken.has(String(b._id)));
  }, [isEdit, parentEntity, borrowers]);

  /**
   * Per LOAN_ENTITIES_GUIDE.md §9: seed `roles` from `title` on first edit so
   * legacy records without roles back-fill into the multi-select. The two are
   * not synced server-side, so it happens here, at form-open time.
   */
  const initialValues = useMemo(() => {
    if (!isEdit || !member) return {};
    const hasRoles = Array.isArray(member.roles) && member.roles.length > 0;
    return {
      ...member,
      roles: hasRoles ? member.roles : member.title ? [member.title] : [],
    };
  }, [isEdit, member]);

  const [values, setValues] = useState(initialValues);

  // A newly opened panel always starts on a clean Create New.
  useEffect(() => {
    setValues(initialValues);
    setAddType(MEMBER_ADD_TYPES.NEW);
    setImportedFrom(null);
  }, [initialValues, mode, entityId]);

  const setValue = useCallback(
    (name, v) => setValues((prev) => ({ ...prev, [name]: v })),
    [],
  );

  // The borrower this member IS, if any — same resolution the chart uses.
  const linkedBorrower = useMemo(
    () => (isEdit ? findLinkedBorrower(member, parentEntity, borrowers) : null),
    [isEdit, member, parentEntity, borrowers],
  );

  // Same name, no link — warn rather than pretend.
  const unlinkedBorrowerMatch = useMemo(
    () =>
      isEdit && !linkedBorrower ? findBorrowerByName(member, borrowers) : null,
    [isEdit, linkedBorrower, member, borrowers],
  );

  const handleImportBorrower = useCallback((borrower) => {
    setValues((prev) => ({ ...prev, ...mapBorrowerToMemberValues(borrower) }));
    setImportedFrom(borrowerDisplayName(borrower));
  }, []);

  // Either card clears the import: Create New means a blank form, and
  // re-picking Import means choosing a borrower again.
  const handleChooseAddType = useCallback((next) => {
    setAddType(next);
    setValues({});
    setImportedFrom(null);
  }, []);

  const sections = useMemo(() => memberSections(), []);
  const ctx = useMemo(() => ({ values }), [values]);
  const required = useMemo(() => requiredOf(sections, ctx), [sections, ctx]);
  const requiredFilled = countRequiredFilled(required, values);
  const hasChanges = isEdit
    ? Object.keys(changedOnly(values, initialValues)).length > 0
    : true;
  const canSave = requiredFilled === required.length && hasChanges;

  const isImporting = !isEdit && addType === MEMBER_ADD_TYPES.IMPORT;
  const showForm = isEdit || !isImporting || !!importedFrom;

  const onSubmit = useCallback(() => {
    if (!canSave) return;
    // Keys that must not appear in the `$set` payload:
    //  - `_id` is immutable; Mongo rejects `$set` on it, and the member id is
    //    already in the URL path
    //  - `middleInitial` is a Pydantic @computed_field derived from
    //    middleName on the server; sending it can clobber that derivation
    const payload = stripKeys(
      isEdit ? changedOnly(values, initialValues) : prune(values),
      ["_id", "middleInitial"],
    );
    if (isEdit) {
      updateLoanEntityMember({
        loanId,
        entityId,
        memberId: member?._id,
        data: payload,
        onSuccessFn: (response) => {
          // The member and the borrower are the same person, and the backend
          // does NOT fan an edit from one to the other — so mirror the fields
          // both models carry in a second POST. The borrower call returns the
          // newer deal, so that response wins; if it fails we still commit the
          // member write rather than dropping it.
          const mirrored = pickBorrowerMirroredFields(payload);
          if (linkedBorrower?._id && !isEmpty(mirrored)) {
            updateBorrower({
              loanId,
              borrowerId: linkedBorrower._id,
              data: mirrored,
              onSuccessFn: (borrowerResponse) => {
                dispatch(loanDrilldownSet(borrowerResponse || response));
                onClose();
              },
              onFailFn: () => {
                dispatch(loanDrilldownSet(response));
                onClose();
              },
            });
            return;
          }
          dispatch(loanDrilldownSet(response));
          onClose();
        },
      });
      return;
    }
    // CREATE never sends an _id — the member is its own document and the
    // backend mints it.
    //
    // NOT LINKED ON PURPOSE (pending a backend decision): the association POST
    // that would record the borrower⇄entity link is held back. Until then an
    // imported member is a COPY of the borrower's details, not a reference to
    // them — the "not linked" notice on the member says so.
    createLoanEntityMember({
      loanId,
      entityId,
      data: payload,
      onSuccessFn: (created) => {
        dispatch(loanDrilldownSet(created));
        onClose();
      },
    });
  }, [
    canSave,
    isEdit,
    values,
    initialValues,
    loanId,
    entityId,
    member?._id,
    linkedBorrower,
    createLoanEntityMember,
    updateLoanEntityMember,
    updateBorrower,
    dispatch,
    onClose,
  ]);

  return (
    <ResizeableSidebar
      hideBackdrop
      open={!!dialog}
      onClose={onClose}
      title={isEdit ? "Edit Member" : "Add Member"}
      defaultWidth={ENTITY_SIDEBAR_WIDTH}
      // `titleIcon`, not `icon` — ResizeableSidebar has no `icon` prop, so the
      // old one rendered nothing.
      titleIcon={
        <Box sx={formSx.panelHeadIcon}>
          <PersonOutlined />
        </Box>
      }
      sx={formSx.panelContainer}
      headerSx={formSx.panelHead}
      bodyPadding={0}
      bodySx={formSx.panelBody}
      headerEndContent={
        isEdit && onDelete ? (
          <Tooltip title="Remove Individual">
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteOutlineOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : null
      }
      footerContent={
        showForm ? (
          <FormPanelFooter
            required={required.length}
            filled={requiredFilled}
            readyText={
              isEdit
                ? hasChanges
                  ? "Ready to save"
                  : "No changes yet"
                : "Ready to add"
            }
          >
            <Button
              variant="outlined"
              color="gray"
              sx={formSx.button}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              sx={formSx.button}
              disabled={!canSave || loading}
              onClick={onSubmit}
            >
              {isEdit ? "Save changes" : "Add member"}
            </Button>
          </FormPanelFooter>
        ) : null
      }
    >
      {dialog && (
        <Box>
          {isEdit && linkedBorrower ? (
            <Box sx={{ ...formSx.panelSection, pt: 0 }}>
              <Flx
                ac
                g={1}
                sx={{
                  px: 1.75,
                  py: 1.25,
                  borderRadius: "8px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                }}
              >
                <PeopleOutlined sx={{ fontSize: 16, color: "#1d4ed8" }} />
                <Txt sx={{ fontSize: 12.5, color: "#1e3a8a" }}>
                  Linked to borrower{" "}
                  <Txt component="span" sx={{ fontWeight: 600 }}>
                    {linkedBorrower.fullName ||
                      [linkedBorrower.firstName, linkedBorrower.lastName]
                        .filter(Boolean)
                        .join(" ") ||
                      "on this loan"}
                  </Txt>
                  . Name, ownership % and guarantor changes save to both
                  records.
                </Txt>
              </Flx>
            </Box>
          ) : null}

          {isEdit && !linkedBorrower && unlinkedBorrowerMatch ? (
            <Box sx={{ ...formSx.panelSection, pt: 0 }}>
              <NotLinkedNotice name={personFullName(unlinkedBorrowerMatch)} />
            </Box>
          ) : null}

          {/* ONE stack, so the section rhythm runs from the chooser through
              the form. */}
          <Box>
            {!isEdit ? (
              <Box sx={formSx.panelSection}>
                <FormMethodCards
                  question="How would you like to add this individual?"
                  value={addType}
                  onChange={handleChooseAddType}
                  options={[
                    {
                      value: MEMBER_ADD_TYPES.NEW,
                      title: MEMBER_ADD_TYPES.NEW,
                      description:
                        "Create and add a new individual on this entity from scratch.",
                      icon: <AddOutlined />,
                    },
                    {
                      value: MEMBER_ADD_TYPES.IMPORT,
                      title: MEMBER_ADD_TYPES.IMPORT,
                      description:
                        "Copy an existing borrower on this loan in as a member.",
                      icon: <PeopleOutlined />,
                    },
                  ]}
                />
              </Box>
            ) : null}

            {/* Import, nothing picked yet — the borrower list. Once one is
                picked the list is replaced by the prefilled form, and Import
                Borrower stays the selected card. */}
            {isImporting && !importedFrom ? (
              <Box sx={formSx.panelSection}>
                <BorrowerImportList
                  borrowers={importableBorrowers}
                  onSelect={handleImportBorrower}
                />
              </Box>
            ) : null}

            {isImporting && importedFrom ? (
              <Box sx={formSx.panelSection}>
                <Flx ac jb>
                  <Txt sx={formSx.sectionHelper}>
                    Imported from{" "}
                    <Txt component="span" sx={{ fontWeight: 600, color: FORM.color.ink }}>
                      {importedFrom}
                    </Txt>
                    . Review and complete the fields below — title and roles
                    have no borrower source.
                  </Txt>
                  <Button
                    size="small"
                    variant="text"
                    color="gray"
                    sx={{ ...formSx.button, flexShrink: 0 }}
                    onClick={() => {
                      setValues({});
                      setImportedFrom(null);
                    }}
                  >
                    Choose another
                  </Button>
                </Flx>
              </Box>
            ) : null}

            {showForm ? (
              <PanelSections sections={sections} ctx={ctx} setValue={setValue} />
            ) : null}
          </Box>
        </Box>
      )}
    </ResizeableSidebar>
  );
};
